// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Бриф — marketing assistant. The content pools below are kept as a graceful
// FALLBACK (used only when the real Hermes pipeline is unreachable), so a local
// demo still renders something. The live wiring (chat-plan → suggestions → pick →
// channels → create → preflight → publish) lives in useBrief.ts and renders the
// new assistant card kinds defined here in the SAME warm Бриф card style.

import type {
  ChatChannelOption,
  ChatIntent,
  MarketingSkill,
  Platform,
  PromotionSuggestion,
  PublishResult,
  SourceMode,
  SourceType,
  Strategy,
} from './promo/types';

export type BriefMode = 'text' | 'creative' | 'idea' | 'image' | 'calendar';

export type MessageKind =
  | 'thinking'
  | 'plain'
  | 'copy'
  | 'image'
  | 'idea'
  | 'calendar'
  // ── live Hermes pipeline kinds ──────────────────────────────────
  | 'plan' // the derived campaign plan (chat-plan result)
  | 'suggestions' // suggestion-picker cards (suggestions result)
  | 'channels' // channel-selection card (direct + buffer)
  | 'draft' // a picked suggestion's per-platform draft preview
  | 'publish' // publish-result summary
  | 'error'; // surfaced API error bubble

export interface CopyVariant {
  tone: string;
  headline: string;
  body: string;
  cta: string;
  meta: string;
}

export interface BriefIdea {
  name: string;
  big: string;
  channels: string[];
  hook: string;
  kpi: string;
}

export type CalendarItemType = 'reels' | 'value' | 'engage' | 'product' | 'promo';

export interface CalendarItem {
  platform: string;
  title: string;
  type: CalendarItemType;
}

export interface CalendarDay {
  day: string;
  date: string;
  items: CalendarItem[];
}

export interface BriefImage {
  label: string;
}

// ── Live Hermes pipeline payloads (rendered as warm Бриф cards) ──

/** The derived campaign plan shown after chat-plan resolves. */
export interface PlanCard {
  language: 'ru' | 'en';
  sourceType: SourceType;
  sourceMode: SourceMode;
  strategy: Strategy;
  marketingSkill: MarketingSkill;
  topic: string;
  platforms: Platform[];
  requireCover: boolean;
  requireDeepLink: boolean;
  fallback?: boolean;
  warning?: string;
}

/** Suggestion-picker state: the candidate cards + which one is chosen. */
export interface SuggestionsCard {
  suggestions: PromotionSuggestion[];
  pickedId: string | null;
  provider?: string;
  fallback?: boolean;
  note?: string;
}

/** Channel-selection state for a picked suggestion. */
export interface ChannelsCard {
  suggestionId: string;
  options: ChatChannelOption[];
  selected: string[]; // ChatChannelOption.id values
  loading: boolean;
  confirmed: boolean;
  error?: string;
  /** Id of the sibling `draft` message, so publish can pick up operator edits (B5). */
  draftMessageId?: number;
}

/** Per-platform draft preview for the picked suggestion. */
export interface DraftCard {
  suggestionId: string;
  angle: string;
  platforms: Array<{ platform: Platform; title?: string; body: string; tags?: string[] }>;
  /**
   * Operator edits keyed by platform (B5). When present for a platform, the
   * edited body overrides the suggestion's rendered draft at publish time. The
   * default (unedited) path leaves this empty, so nothing changes.
   */
  edits?: Partial<Record<Platform, string>>;
  /** Which platform's inline editor is currently open (UI-only). */
  editing?: Platform | null;
  /** Appended hashtag chips keyed by platform (B7). Display-only convenience. */
  extraTags?: Partial<Record<Platform, string[]>>;
  /** Translated body per platform (B7). Shown as a toggled block. */
  translated?: Partial<Record<Platform, { language: 'ru' | 'en'; body: string }>>;
}

/** Publish outcome summary. */
export interface PublishCard {
  postId?: string;
  results: PublishResult[];
  message?: string;
  status?: 'creating' | 'preflight' | 'publishing' | 'done' | 'blocked';
}

export interface BriefMessage {
  id: number;
  role: 'user' | 'assistant';
  kind: MessageKind;
  mode?: BriefMode;
  text?: string;
  variants?: CopyVariant[];
  ideas?: BriefIdea[];
  calendar?: CalendarDay[];
  images?: BriefImage[];
  creative?: boolean;
  prompt?: string;
  // ── live pipeline payloads ─────────────────────────────────────
  plan?: PlanCard;
  picker?: SuggestionsCard;
  channels?: ChannelsCard;
  draft?: DraftCard;
  publish?: PublishCard;
}

// ── Intent presets — map the active composer mode to plan defaults ──
//
// Each Бриф mode maps to a ChatIntent and the plan fields the suggestions/
// chat-plan endpoints expect. These are the seed defaults; chat-plan may refine
// them (and we merge its result over these).

export interface IntentPreset {
  intent: ChatIntent;
  sourceType: SourceType;
  sourceMode: SourceMode;
  strategy: Strategy;
  marketingSkill: MarketingSkill;
  requireCover: boolean;
  requireDeepLink: boolean;
}

export const INTENT_PRESETS: Record<BriefMode, IntentPreset> = {
  // Текст — operator-picked fresh selection, copy-led.
  text: {
    intent: 'selection',
    sourceType: 'all',
    sourceMode: 'latest',
    strategy: 'auto',
    marketingSkill: 'smart_fomo',
    requireCover: false,
    requireDeepLink: true,
  },
  // Креатив — cover-forward, FOMO angle.
  creative: {
    intent: 'selection',
    sourceType: 'all',
    sourceMode: 'popular',
    strategy: 'fomo',
    marketingSkill: 'smart_fomo',
    requireCover: true,
    requireDeepLink: true,
  },
  // Идея — book-authority insight angle.
  idea: {
    intent: 'book',
    sourceType: 'book',
    sourceMode: 'world_books',
    strategy: 'insight',
    marketingSkill: 'authority_books',
    requireCover: false,
    requireDeepLink: true,
  },
  // Изображение — image generation is disabled; reuse existing covers via a
  // cover-forward fresh-library pick.
  image: {
    intent: 'collection',
    sourceType: 'all',
    sourceMode: 'popular',
    strategy: 'trend',
    marketingSkill: 'fresh_library',
    requireCover: true,
    requireDeepLink: true,
  },
  // Календарь — topic-angle collection for a content plan.
  calendar: {
    intent: 'collection',
    sourceType: 'all',
    sourceMode: 'topic',
    strategy: 'collection',
    marketingSkill: 'topic_angle',
    requireCover: false,
    requireDeepLink: true,
  },
};

export interface ModeDef {
  id: BriefMode;
  label: string;
}

export interface SuggestionDef {
  mode: BriefMode;
  tag: string;
  title: string;
  q: string;
}

export interface ChatDef {
  title: string;
  tag: string;
  mode: BriefMode;
  q: string;
}

export const MODES: ModeDef[] = [
  { id: 'text', label: 'Текст' },
  { id: 'creative', label: 'Креатив' },
  { id: 'idea', label: 'Идея' },
  { id: 'image', label: 'Изображение' },
  { id: 'calendar', label: 'Календарь' },
];

// Generic starter prompts — they invoke the REAL pipeline (startSend), they are
// not demo data. On-brand for Summio (book/YouTube summaries), no fake campaign.
export const SUGGESTIONS: SuggestionDef[] = [
  { mode: 'text', tag: 'Тексты', title: 'Посты по свежему саммари из библиотеки', q: 'Сделай посты по свежему саммари из библиотеки' },
  { mode: 'creative', tag: 'Креатив', title: 'Кандидат под актуальный инфоповод', q: 'Подбери кандидата под актуальный инфоповод' },
  { mode: 'idea', tag: 'Идея', title: 'Концепция кампании по бестселлеру', q: 'Предложи концепцию кампании по популярному саммари' },
  { mode: 'image', tag: 'Изображение', title: 'Визуально сильное саммари для сторис', q: 'Подбери визуально сильное саммари для сторис' },
  { mode: 'calendar', tag: 'Календарь', title: 'Контент-план на неделю по библиотеке', q: 'Собери контент-план на неделю по библиотеке' },
];

// Recent chats are not persisted yet — no demo entries. The "Недавние" section
// stays hidden until real chat history exists.
export const CHATS: ChatDef[] = [];

const COPY_POOL: CopyVariant[] = [
  { tone: 'Энергичный', headline: 'Лето не ждёт — и скидки тоже', body: 'Новая летняя коллекция уже здесь. До конца недели — −30% на всё. Успейте собрать образ, пока размеры в наличии.', cta: 'Смотреть коллекцию', meta: 'Instagram · 2200 зн.' },
  { tone: 'Спокойный', headline: 'Лёгкость, которую хочется носить', body: 'Дышащие ткани, чистые силуэты, оттенки сезона. Коллекция, созданная для города и отпуска одновременно.', cta: 'Открыть новинки', meta: 'Telegram · 1100 зн.' },
  { tone: 'Прямой', headline: '−30% на летнюю коллекцию', body: 'Только до воскресенья. Без промокодов и условий — скидка применяется в корзине автоматически.', cta: 'Перейти в каталог', meta: 'VK · 800 зн.' },
  { tone: 'Игривый', headline: 'Гардероб, который сам просится в отпуск', body: 'Собрали то, в чём не жарко и всегда стильно. Минус 30% — повод обновиться уже сегодня.', cta: 'Хочу новинки', meta: 'Instagram · 1500 зн.' },
  { tone: 'Премиальный', headline: 'Сезон, отмеренный до детали', body: 'Сдержанная палитра, выверенный крой, ограниченный тираж. Летняя коллекция — со скидкой 30% эту неделю.', cta: 'Ознакомиться', meta: 'Сайт · 900 зн.' },
];

const IDEA_POOL: BriefIdea[] = [
  { name: 'Первый шаг', big: 'Показываем, как приложение убирает рутину за один день жизни пользователя — серия коротких историй «до и после».', channels: ['Reels', 'Influencers', 'ASO'], hook: 'Скачай — и закрой задачу за 60 секунд', kpi: 'CPI / установки' },
  { name: 'Сделано вместе', big: 'UGC-движение: пользователи показывают свои результаты с продуктом, лучшие попадают в рекламу и получают подписку.', channels: ['UGC', 'TikTok', 'Email'], hook: 'Твой результат — наша обложка', kpi: 'Engagement / CAC' },
  { name: 'Тихий старт', big: 'Закрытый ранний доступ по приглашениям, создаём ощущение клуба и дефицита перед публичным релизом.', channels: ['Waitlist', 'Telegram', 'PR'], hook: 'Войти раньше всех — по приглашению', kpi: 'Waitlist → retention' },
  { name: 'Один экран', big: 'Бьём в одну боль аудитории и строим всю кампанию вокруг неё: единый месседж, единый визуал, разные форматы.', channels: ['Performance', 'OOH', 'Лендинг'], hook: 'Одна проблема. Одно решение.', kpi: 'CTR / конверсия' },
];

export const CALENDAR_DATA: CalendarDay[] = [
  { day: 'Пн', date: '01.07', items: [{ platform: 'Instagram', title: 'Reels: распаковка новинки сезона', type: 'reels' }] },
  { day: 'Вт', date: '02.07', items: [{ platform: 'Telegram', title: 'Пост-польза: 5 ошибок в уходе', type: 'value' }, { platform: 'Stories', title: 'Опрос: ваш тип кожи', type: 'engage' }] },
  { day: 'Ср', date: '03.07', items: [{ platform: 'VK', title: 'Карусель: состав бестселлера', type: 'product' }] },
  { day: 'Чт', date: '04.07', items: [{ platform: 'Instagram', title: 'UGC: отзыв клиента + до/после', type: 'engage' }] },
  { day: 'Пт', date: '05.07', items: [{ platform: 'Telegram', title: 'Анонс: −20% на выходные', type: 'promo' }, { platform: 'Reels', title: 'Рутина дня с продуктом', type: 'reels' }] },
  { day: 'Сб', date: '06.07', items: [{ platform: 'Stories', title: 'Закулисье: как мы тестируем', type: 'value' }] },
  { day: 'Вс', date: '07.07', items: [{ platform: 'Дайджест', title: 'Итоги недели + напоминание об акции', type: 'promo' }] },
];

export const DOT_COLORS: Record<CalendarItemType, string> = {
  reels: '#1a1916',
  value: '#8a8a85',
  engage: '#b08968',
  product: '#5f5c55',
  promo: '#c1442e',
};

export const INTROS: Record<BriefMode, string> = {
  text: 'Вот три варианта поста — разный тон под разные сегменты. Любой можно доработать.',
  creative: 'Подготовил баннеры под основные форматы. Выберите направление — сделаю версии под остальные площадки.',
  idea: 'Три концепции под запрос. У каждой — большая идея, каналы и крючок для первой недели.',
  image: 'Сгенерировал 4 варианта по запросу. Выберите понравившийся — доработаю композицию.',
  calendar: 'Контент-план на неделю. Сбалансировал форматы: вовлечение, продукт и польза.',
};

export const THINKING_LABELS: Record<BriefMode, string> = {
  text: 'Подбираю формулировки…',
  creative: 'Собираю креативы…',
  idea: 'Прорабатываю концепции…',
  image: 'Генерирую изображения…',
  calendar: 'Составляю план…',
};

function pick<T>(arr: T[], n: number): T[] {
  return [...arr].sort(() => Math.random() - 0.5).slice(0, n);
}

export function buildContent(mode: BriefMode, text: string, aid: number): BriefMessage {
  const base: BriefMessage = { id: aid, role: 'assistant', kind: 'plain', mode, text: INTROS[mode] || 'Готово.' };
  if (mode === 'text') return { ...base, kind: 'copy', variants: pick(COPY_POOL, 3) };
  if (mode === 'idea') return { ...base, kind: 'idea', ideas: pick(IDEA_POOL, 3) };
  if (mode === 'calendar') return { ...base, kind: 'calendar', calendar: CALENDAR_DATA };
  if (mode === 'creative') {
    const fmts = ['1080×1080', '1080×1920', '1200×628', '1080×1350'];
    return { ...base, kind: 'image', creative: true, prompt: text, images: fmts.map((f) => ({ label: f })) };
  }
  // image
  return { ...base, kind: 'image', creative: false, prompt: text, images: [0, 1, 2, 3].map(() => ({ label: '1024×1024' })) };
}

// ── Live-pipeline helpers ────────────────────────────────────────

const PLATFORM_LABELS: Record<Platform, string> = {
  telegram: 'Telegram',
  twitter: 'X (Twitter)',
  instagram: 'Instagram',
  threads: 'Threads',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  reddit: 'Reddit',
  producthunt: 'Product Hunt',
};

export function platformLabel(p: Platform): string {
  return PLATFORM_LABELS[p] ?? p;
}

const STRATEGY_LABELS: Record<Strategy, string> = {
  auto: 'Авто',
  fomo: 'FOMO',
  insight: 'Инсайт',
  trend: 'Тренд',
  comparison: 'Сравнение',
  collection: 'Подборка',
};

export function strategyLabel(s: Strategy): string {
  return STRATEGY_LABELS[s] ?? s;
}

const SOURCE_MODE_LABELS: Record<SourceMode, string> = {
  latest: 'Свежее',
  popular: 'Популярное',
  world_books: 'Мировые книги',
  topic: 'По теме',
};

export function sourceModeLabel(m: SourceMode): string {
  return SOURCE_MODE_LABELS[m] ?? m;
}

/** Build a per-platform draft preview from a suggestion's drafts map. */
export function draftCardFromSuggestion(s: PromotionSuggestion): DraftCard {
  const platforms: DraftCard['platforms'] = [];
  for (const platform of Object.keys(s.drafts) as Platform[]) {
    const d = s.drafts[platform];
    if (!d) continue;
    // PlatformDraft stores the copy in `text` (not `body`); reading `body` left
    // the draft-preview empty and B5 with nothing to edit.
    const body = typeof d.text === 'string' ? d.text : '';
    if (body.length === 0) continue;
    platforms.push({ platform, body });
  }
  return { suggestionId: s.id, angle: s.angle, platforms };
}

/**
 * Graceful FALLBACK suggestions when the API is unreachable. We synthesise one
 * candidate per copy-pool entry so the picker still demonstrates the flow. These
 * carry no real summaries; picking one renders the same channel/draft cards.
 */
export function fallbackSuggestions(prompt: string, language: 'ru' | 'en'): PromotionSuggestion[] {
  return pick(COPY_POOL, 3).map((v, i): PromotionSuggestion => {
    const body = v.body;
    const source = {
      title: v.headline,
      body,
      tags: [] as string[],
      language,
    };
    const draft = { title: v.headline, body, tags: [] as string[] };
    return {
      id: `demo-${Date.now()}-${i}`,
      language,
      source_type: 'all',
      angle: v.headline,
      rationale: `${v.tone} тон · ${v.meta}. Демо-кандидат (офлайн-режим), запрос: ${prompt.slice(0, 48)}`,
      source,
      summaries: [],
      drafts: {
        telegram: { ...draft },
        twitter: { ...draft },
      } as PromotionSuggestion['drafts'],
    };
  });
}

/** Detect campaign language from the prompt (cheap heuristic, RU default). */
export function detectLanguage(prompt: string): 'ru' | 'en' {
  return /[а-яё]/i.test(prompt) ? 'ru' : 'en';
}
